import React, { useEffect } from "react";
import { HashRouter, Route, Routes, Navigate, useNavigate } from "react-router-dom";
import PrivateRoute from "./components/PrivateRoute";
import RouteScrollToTop from "./helper/RouteScrollToTop";
import { AuthProvider } from "./contexts/AuthContext";
import { ReportsProvider } from "./contexts/ReportsContextUpdated";
import { ProductProvider } from "./contexts/ProductContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import { ToastProvider } from "./contexts/ToastContext";
import notificationNavigation from './utils/NotificationNavigation';

// Páginas de autenticação
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import WelcomePage from "./pages/WelcomePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import MasterLayout from "./masterLayout/MasterLayout";

// Dashboard principal
import HomePageTen from "./pages/HomePageTen";

// Páginas de perfil
import ViewProfilePage from "./pages/ViewProfilePage";

// Páginas de responsáveis
import ResponsiblesPage from "./pages/ResponsiblesPage";
import ResponsibleFormPage from "./pages/ResponsibleFormPage";
import ResponsibleCompletePage from "./pages/ResponsibleCompletePage";

// Páginas de produtos
import ProductsListPage from "./pages/products/ProductsListPage";
import ProductDetailsPage from "./pages/products/ProductDetailsPage";
import ProductEditPage from "./pages/products/ProductEditPage";
import ProductCreatePage from "./pages/products/ProductCreatePage";

// Páginas de afiliados
import MyAffiliatesPage from "./pages/affiliate/MyAffiliatesPage";
import MyAffiliateRequestsPage from "./pages/affiliate/MyAffiliateRequestsPage";
import MyAffiliateProductsPage from "./pages/affiliate/MyAffiliateProductsPage";
import ProductsAffiliates from "./pages/affiliate/ProductsAffiliates";
import AffiliatesOfMyProductsPage from "./pages/affiliate/AffiliatesOfMyProductsPage";
import AffiliateProductDetailsPage from "./pages/affiliate/AffiliateProductDetailsPage";
import SellerProductsPage from "./pages/products/SellerProductsPage";

// Páginas de gerentes
import ProductsManaged from "./pages/manager/ProductsManaged";

// Páginas de vendas
import SalesManagementPage from "./pages/sales/SalesManagementPage";
import AfterPayPage from "./pages/sales/AfterPayPage";

// Páginas de bancos
import BanksPage from "./pages/banks/Banks";
import WithdrawalsPage from "./pages/banks/Withdrawals";

// Páginas de relatórios
import AfterPayReportsPage from "./pages/reports/AfterPayReportsPage";
import ReportsMainPage from "./pages/reports/ReportsMainPage";
import ExecutiveDashboard from "./pages/reports/ExecutiveDashboard";
import SalesReport from "./pages/reports/SalesReport";
import CommissionsReport from "./pages/reports/CommissionsReport";
import AffiliatePerformanceReport from "./pages/reports/AffiliatePerformanceReport";
import ProductsReport from "./pages/reports/ProductsReport";
import AfterPayReport from "./pages/reports/AfterPayReport";
import ClientsReport from "./pages/reports/ClientsReport";
import FinancialReport from "./pages/reports/FinancialReport";
import CouponsReport from "./pages/reports/CouponsReport";
import ChurnReport from "./pages/reports/ChurnReport";
import AbandonmentsReport from "./pages/reports/AbandonmentsReport";
import RefundsReport from "./pages/reports/RefundsReport";
import TeamsReport from "./pages/reports/TeamsReport";

// Páginas de comissões
import CommissionDashboard from "./pages/commissions/CommissionDashboard";


// Páginas de compras
import MyPurchasesPage from "./pages/purchases/MyPurchasesPage";

// Páginas de checkout
import PublicCheckout from "./pages/checkout/PublicCheckout";
import LandingCheckout from "./pages/checkout/LandingCheckout";
import SecureCheckout from "./pages/checkout/SecureCheckout";
import CheckoutSuccessPage from "./pages/checkout/CheckoutSuccessPage";
import CheckoutProcessingPage from "./pages/checkout/CheckoutProcessingPage";
import SaleCheckoutPaymentPage from "./pages/checkout/SaleCheckoutPaymentPage";

// Páginas de delivery
import PendingPage from "./pages/delivery/Pending";
import ForwardedPage from "./pages/delivery/Forwarded";
import CompletedPage from "./pages/delivery/Completed";

// Páginas de admin
import AdminOrdersPage from "./pages/admin/AdminOrdersPage";
import AdminTicketsPage from "./pages/admin/AdminTicketsPage";
import WithdrawalAdminPage from "./pages/admin/WithdrawalAdminPage";
import AdminUsersList from "./components/admin/AdminUsersList";
import AdminApprovalsList from "./components/admin/AdminApprovalsList";
import AdminBankAccountsPage from "./pages/admin/AdminBankAccountsPage";
import AdminCustomersPage from "./pages/admin/AdminCustomersPage";
import AdminPermissionsPage from "./pages/admin/AdminPermissionsPage";
import AdminProductsApprovalPage from "./pages/admin/AdminProductsApprovalPage";
import AdminConfigPage from "./pages/admin/AdminConfigPage";
import PlatformFeePlansPage from "./pages/admin/PlatformFeePlansPage";
import AdvancePaymentPlansPage from "./pages/admin/AdvancePaymentPlansPage";
import MinimumWithdrawalPlansPage from "./pages/admin/MinimumWithdrawalPlansPage";
import AdminDashboard from "./components/admin/AdminDashboard";

// Páginas de tickets
import UserTicketsPage from "./pages/tickets/UserTicketsPage";

// Páginas de store
import ProductGridPage from "./pages/store/ProductGridPage";
import StoreProductDetailsPage from "./pages/store/ProductDetailsPage";

// Páginas de ferramentas/operações
import ApiPage from "./pages/tools/ApiPage";
import PostbackPage from "./pages/tools/PostbackPage";
import FreightPage from "./pages/tools/FreightPage";
import TeamPage from "./pages/tools/TeamPage";
import PermissionGroupsPage from "./pages/tools/PermissionGroupsPage";
import WebhooksPage from "./pages/webhooks/WebhooksPage";

// Wiki
import WikiPage from "./pages/wiki/WikiPage";

// Páginas de integração
import IntegrationPage from "./pages/integration/IntegrationPage";
import IntegrationsPage from "./pages/integration/IntegrationsPage";
import BlingIntegrationPage from "./pages/integration/BlingIntegrationPage";
import BlingIntegrationPageV2 from "./pages/integration/BlingIntegrationPageV2";
import BlingCallback from "./pages/integration/BlingCallback";
import NotazzIntegrationPage from "./pages/integration/NotazzIntegrationPage";

// Teste AfterPay
import TestAfterPay from "./pages/TestAfterPay";

// Componente para inicializar navegação
const NavigationInitializer = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Inicializar apenas uma vez
    if (!notificationNavigation.navigate) {
      notificationNavigation.initialize(navigate);
    }
  }, [navigate]);
  
  return null;
};

function App() {
  return (
    <AuthProvider>
      <ProductProvider>
          <ReportsProvider>
            <NotificationProvider>
              <ToastProvider>
                <ConfirmProvider>
              <HashRouter future={{ v7_relativeSplatPath: true }}>
                <NavigationInitializer />
                <RouteScrollToTop />
                <Routes>
                  {/* Rotas Públicas de Autenticação */}
                  <Route exact path="/" element={<SignInPage />} />
                  <Route exact path="/login" element={<SignInPage />} />
                  <Route exact path="/sign-up" element={<SignUpPage />} />
                  <Route exact path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route exact path="/accept-invite/:token" element={<AcceptInvitePage />} />

                  {/* Rotas Protegidas - Dashboard */}
                  <Route
                    exact
                    path="/dashboard"
                    element={
                      <PrivateRoute>
                        <HomePageTen />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Perfil */}
                  <Route
                    exact
                    path="/view-profile"
                    element={
                      <PrivateRoute>
                        
                          <ViewProfilePage />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Responsáveis */}
                  <Route
                    exact
                    path="/responsibles"
                    element={
                      <PrivateRoute>
                        
                          <ResponsiblesPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/responsibles/new"
                    element={
                      <PrivateRoute>
                        
                          <ResponsibleFormPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/responsibles/edit/:id"
                    element={
                      <PrivateRoute>
                        
                          <ResponsibleFormPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/responsibles/complete/:id"
                    element={
                      <PrivateRoute>
                        
                          <ResponsibleCompletePage />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Produtos */}
                  {/* ⚠️ IMPORTANTE: Rotas estáticas ANTES de rotas dinâmicas! */}
                  {/* Alias /products → /products/products. Sem esse redirect,
                      navigate('/products') em CreatePage/EditPage caía no
                      catch-all e jogava o user pra login. */}
                  <Route path="/products" element={<Navigate to="/products/products" replace />} />
                  <Route
                    exact
                    path="/products/products"
                    element={
                      <PrivateRoute>

                          <ProductsListPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/products/registration/new"
                    element={
                      <PrivateRoute>
                          <ProductCreatePage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/products/affiliates"
                    element={
                      <PrivateRoute>
                        
                          <ProductsAffiliates />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/products/seller"
                    element={
                      <PrivateRoute>
                        <SellerProductsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/affiliate/products/:id"
                    element={
                      <PrivateRoute>
                        <AffiliateProductDetailsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas de Gerente de Afiliados */}
                  <Route
                    exact
                    path="/manager/products"
                    element={
                      <PrivateRoute>
                        <ProductsManaged />
                      </PrivateRoute>
                    }
                  />
                  {/* Rotas dinâmicas DEPOIS das estáticas */}
                  {/* Edit ANTES do :id pra evitar match contra UUID */}
                  <Route
                    exact
                    path="/products/:id/edit"
                    element={
                      <PrivateRoute>
                        <ProductEditPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/products/:id"
                    element={
                      <PrivateRoute>
                        <ProductDetailsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Públicas de Checkout */}
                  {/* ⚠️ IMPORTANTE: Rotas estáticas ANTES de rotas dinâmicas! */}
                  <Route
                    exact
                    path="/checkout/processing"
                    element={<CheckoutProcessingPage />}
                  />
                  <Route
                    exact
                    path="/checkout/success"
                    element={<CheckoutSuccessPage />}
                  />
                  <Route
                    exact
                    path="/checkout-success"
                    element={<CheckoutSuccessPage />}
                  />
                  {/* Rotas dinâmicas DEPOIS das estáticas */}
                  <Route
                    exact
                    path="/checkout/:productId/:checkoutId"
                    element={<LandingCheckout />}
                  />
                  <Route
                    exact
                    path="/checkout/:productId"
                    element={<LandingCheckout />}
                  />
                  <Route
                    exact
                    path="/secure-checkout/:productId/:checkoutId"
                    element={<SecureCheckout />}
                  />
                  <Route
                    exact
                    path="/checkout-payment/:saleId"
                    element={<SaleCheckoutPaymentPage />}
                  />

                  {/* Rotas Protegidas - Afiliados */}
                  <Route
                    exact
                    path="/my-affiliates"
                    element={
                      <PrivateRoute>
                        
                          <MyAffiliatesPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/my-affiliate-requests"
                    element={
                      <PrivateRoute>
                        
                          <MyAffiliateRequestsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Vendas */}
                  <Route
                    exact
                    path="/sales-management"
                    element={
                      <PrivateRoute>
                        
                          <SalesManagementPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/afterpay"
                    element={
                      <PrivateRoute>
                        
                          <AfterPayPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/commissions/dashboard"
                    element={
                      <PrivateRoute>
                        
                          <CommissionDashboard />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Bancos/Financeiro */}
                  <Route
                    exact
                    path="/banks"
                    element={
                      <PrivateRoute>
                        
                          <BanksPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/withdrawals"
                    element={
                      <PrivateRoute>
                        
                          <WithdrawalsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Relatórios */}
                  <Route
                    exact
                    path="/reports/executive"
                    element={
                      <PrivateRoute>
                        <ExecutiveDashboard />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/sales"
                    element={
                      <PrivateRoute>
                        <SalesReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/commissions"
                    element={
                      <PrivateRoute>
                        <CommissionsReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/affiliate-performance"
                    element={
                      <PrivateRoute>
                        <AffiliatePerformanceReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/products"
                    element={
                      <PrivateRoute>
                        <ProductsReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/afterpay"
                    element={
                      <PrivateRoute>
                        <AfterPayReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/clients"
                    element={
                      <PrivateRoute>
                        <ClientsReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/financial"
                    element={
                      <PrivateRoute>
                        <FinancialReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/coupons"
                    element={
                      <PrivateRoute>
                        <CouponsReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/churn"
                    element={
                      <PrivateRoute>
                        <ChurnReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/abandonments"
                    element={
                      <PrivateRoute>
                        <AbandonmentsReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/refunds"
                    element={
                      <PrivateRoute>
                        <RefundsReport />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/reports/teams"
                    element={
                      <PrivateRoute>
                        <TeamsReport />
                      </PrivateRoute>
                    }
                  />
                  {/* Rotas Protegidas - Compras */}
                  <Route
                    exact
                    path="/my-purchases"
                    element={
                      <PrivateRoute>
                        
                          <MyPurchasesPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/purchases/pending"
                    element={
                      <PrivateRoute>
                        
                          <PendingPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/purchases/forwarded"
                    element={
                      <PrivateRoute>
                        
                          <ForwardedPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/purchases/completed"
                    element={
                      <PrivateRoute>
                        
                          <CompletedPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Admin */}
                  <Route
                    exact
                    path="/admin/dashboard"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <AdminDashboard />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/orders"
                    element={
                      <PrivateRoute>
                        
                          <AdminOrdersPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/tickets"
                    element={
                      <PrivateRoute>
                        
                          <AdminTicketsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/withdrawals"
                    element={
                      <PrivateRoute>
                        
                          <WithdrawalAdminPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/users"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <AdminUsersList />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/approvals"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <AdminApprovalsList />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/bank-accounts"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <AdminBankAccountsPage />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/approve-products"
                    element={
                      <PrivateRoute>
                        <AdminProductsApprovalPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/customers"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <AdminCustomersPage />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/permissions"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <AdminPermissionsPage />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/config"
                    element={
                      <PrivateRoute>
                        <AdminConfigPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/platform-fee-plans"
                    element={
                      <PrivateRoute>
                        <PlatformFeePlansPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/advance-payment-plans"
                    element={
                      <PrivateRoute>
                        <AdvancePaymentPlansPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/admin/minimum-withdrawal-plans"
                    element={
                      <PrivateRoute>
                        <MinimumWithdrawalPlansPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Tickets */}
                  <Route
                    exact
                    path="/tickets"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <UserTicketsPage />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Welcome/Onboarding */}
                  <Route
                    exact
                    path="/welcome"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <WelcomePage />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Store */}
                  <Route
                    exact
                    path="/store/store-products"
                    element={
                      <PrivateRoute>
                        
                          <ProductGridPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/store/:id"
                    element={
                      <PrivateRoute>
                          <StoreProductDetailsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Operações/Ferramentas */}
                  <Route
                    exact
                    path="/api"
                    element={
                      <PrivateRoute>
                        
                          <ApiPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/webhook"
                    element={
                      <PrivateRoute>
                        
                          <PostbackPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/webhooks"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <WebhooksPage />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/frete"
                    element={
                      <PrivateRoute>
                        
                          <FreightPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/team"
                    element={
                      <PrivateRoute>
                        
                          <TeamPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/permission-groups"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <PermissionGroupsPage />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/wiki"
                    element={
                      <PrivateRoute>
                        <MasterLayout>
                          <WikiPage />
                        </MasterLayout>
                      </PrivateRoute>
                    }
                  />

                  {/* Rotas Protegidas - Integrações */}
                  <Route
                    exact
                    path="/integration"
                    element={
                      <PrivateRoute>
                        
                          <IntegrationsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/integration/bling"
                    element={
                      <PrivateRoute>
                        <BlingIntegrationPageV2 />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/integration/bling/callback"
                    element={
                      <PrivateRoute>

                          <BlingCallback />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/integration/notazz"
                    element={
                      <PrivateRoute>
                        <NotazzIntegrationPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Teste temporário AfterPay */}
                  <Route path="/test-afterpay" element={<TestAfterPay />} />

                  {/* Rota 404 - Redireciona para login */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </HashRouter>
                </ConfirmProvider>
              </ToastProvider>
            </NotificationProvider>
          </ReportsProvider>
      </ProductProvider>
    </AuthProvider>
  );
}

export default App;
